import type {
  CommunityMetadata,
  BadgeAward,
  BadgeReference,
  BadgeTier,
  BadgeHolder,
  AuthorityChain,
  ParsedATag,
} from '@/types/badges';
import { parseATag, parseBadgeAward } from '@/types/badges';

// Find badge reference by a-tag string
export function findBadgeRef(
  community: CommunityMetadata,
  aTagValue: string
): BadgeReference | null {
  const parsed = parseATag(['a', aTagValue]);
  if (!parsed) return null;
  
  return community.badges.find(
    b => b.pubkey === parsed.pubkey && b.identifier === parsed.identifier
  ) || null;
}

// Get the rank of a badge holder (their lowest rank badge)
export function getHolderRank(
  holderPubkey: string,
  community: CommunityMetadata,
  awards: BadgeAward[]
): number | undefined {
  // Check if they're a founder (rank 0)
  if (community.founders.includes(holderPubkey)) {
    return 0;
  }
  
  // Find their badges and get the lowest rank
  const holderBadges = awards.filter(a => {
    const awardData = parseBadgeAward(a);
    return awardData?.recipient === holderPubkey;
  });
  
  let lowestRank = Infinity;
  for (const award of holderBadges) {
    const awardData = parseBadgeAward(award);
    if (!awardData) continue;
    
    const badgeRef = findBadgeRef(community, awardData.badgeRef);
    if (badgeRef && badgeRef.rank < lowestRank) {
      lowestRank = badgeRef.rank;
    }
  }
  
  return lowestRank === Infinity ? undefined : lowestRank;
}

// Validate a single authority chain from a badge award
export function validateAuthorityChain(
  award: BadgeAward,
  community: CommunityMetadata,
  allAwards: BadgeAward[],
  deletedAwards: Set<string>,
  chain: Map<string, BadgeAward> = new Map(),
  depth: number = 0,
  maxDepth: number = 20
): AuthorityChain | null {
  const awardData = parseBadgeAward(award);
  if (!awardData) return null;
  
  const badgeRef = findBadgeRef(community, awardData.badgeRef);
  if (!badgeRef) return null;
  
  const recipient = awardData.recipient;
  const granter = award.pubkey;
  
  // Check if this award is deleted
  if (deletedAwards.has(award.id)) {
    return {
      recipient,
      badge: badgeRef,
      granter,
      isValid: false,
      invalidReason: 'This badge award has been revoked',
    };
  }
  
  // Check if granter is a founder
  if (community.founders.includes(granter)) {
    return {
      recipient,
      badge: badgeRef,
      granter,
      isValid: true,
    };
  }
  
  // Recursive validation - find the granter's badge
  const granterBadges = allAwards.filter(a => {
    const data = parseBadgeAward(a);
    return data?.recipient === granter;
  });
  
  // Find the highest-ranked badge the granter holds
  let granterHighestBadge: BadgeAward | null = null;
  let granterLowestRank = Infinity;
  
  for (const gb of granterBadges) {
    if (deletedAwards.has(gb.id)) continue;
    
    const gbData = parseBadgeAward(gb);
    if (!gbData) continue;
    
    const gbRef = findBadgeRef(community, gbData.badgeRef);
    if (gbRef && gbRef.rank < granterLowestRank) {
      granterLowestRank = gbRef.rank;
      granterHighestBadge = gb;
    }
  }
  
  // Check depth limit
  if (depth >= maxDepth) {
    return {
      recipient,
      badge: badgeRef,
      granter,
      granterBadge: granterHighestBadge ? findBadgeRef(community, parseBadgeAward(granterHighestBadge)?.badgeRef || '') || undefined : undefined,
      isValid: false,
      invalidReason: 'Chain exceeds maximum validation depth',
    };
  }
  
  // Check if granter has no valid badge
  if (!granterHighestBadge) {
    return {
      recipient,
      badge: badgeRef,
      granter,
      isValid: false,
      invalidReason: 'Granter does not hold a valid badge in this community',
    };
  }
  
  // Check rank authority: granter rank must be lower (higher authority) than awarded rank
  const granterBadgeRef = findBadgeRef(community, parseBadgeAward(granterHighestBadge)?.badgeRef || '');
  if (granterBadgeRef && granterBadgeRef.rank >= badgeRef.rank) {
    return {
      recipient,
      badge: badgeRef,
      granter,
      granterBadge: granterBadgeRef,
      isValid: false,
      invalidReason: `Granter rank (${granterBadgeRef.rank}) is not higher than awarded rank (${badgeRef.rank})`,
    };
  }
  
  // Recurse up the chain
  const parentChain = validateAuthorityChain(
    granterHighestBadge,
    community,
    allAwards,
    deletedAwards,
    chain,
    depth + 1,
    maxDepth
  );
  
  return {
    recipient,
    badge: badgeRef,
    granter,
    granterBadge: granterBadgeRef || undefined,
    parentChain: parentChain || undefined,
    isValid: parentChain?.isValid !== false,
    invalidReason: parentChain?.invalidReason,
  };
}

// Build all badge tiers for a community
export function buildBadgeTiers(
  community: CommunityMetadata,
  awards: BadgeAward[],
  deletedAwards: Set<string>
): BadgeTier[] {
  const tiers: BadgeTier[] = [];
  
  // Group badges by rank
  const byRank = new Map<number, BadgeReference[]>();
  for (const badge of community.badges) {
    const existing = byRank.get(badge.rank) || [];
    existing.push(badge);
    byRank.set(badge.rank, existing);
  }
  
  // Build tiers from ranks (0 to highest)
  const ranks = Array.from(byRank.keys()).sort((a, b) => a - b);
  
  for (const rank of ranks) {
    const badges = byRank.get(rank)!;
    
    for (const badge of badges) {
      // Find all holders of this badge
      const holders: BadgeHolder[] = [];
      
      for (const award of awards) {
        const awardData = parseBadgeAward(award);
        if (!awardData) continue;
        
        // Check if this award is for this badge
        const parsed = parseATag(['a', awardData.badgeRef]);
        if (!parsed || parsed.pubkey !== badge.pubkey || parsed.identifier !== badge.identifier) {
          continue;
        }
        
        const recipient = awardData.recipient;
        
        // Build authority chain for this award
        const chain = validateAuthorityChain(award, community, awards, deletedAwards);
        
        // Get or create holder entry
        let holder = holders.find(h => h.pubkey === recipient);
        if (!holder) {
          const rank = getHolderRank(recipient, community, awards);
          holder = {
            pubkey: recipient,
            badges: [],
            authorityChains: [],
            isValid: true,
            highestRank: rank ?? Infinity,
          };
          holders.push(holder);
        }
        
        holder.badges.push(award);
        if (chain) {
          holder.authorityChains.push(chain);
          if (!chain.isValid) {
            holder.isValid = false;
          }
        }
      }
      
      // Sort holders by rank
      holders.sort((a, b) => a.highestRank - b.highestRank);
      
      tiers.push({
        rank,
        badge,
        holders,
      });
    }
  }
  
  return tiers;
}

// Count valid badge holders at each rank level
export function countHoldersByRank(tiers: BadgeTier[]): Map<number, number> {
  const counts = new Map<number, number>();
  
  for (const tier of tiers) {
    const validCount = tier.holders.filter(h => h.isValid).length;
    counts.set(tier.rank, (counts.get(tier.rank) || 0) + validCount);
  }
  
  return counts;
}

// Get all invalid awards and their reasons
export function getInvalidAwards(
  awards: BadgeAward[],
  community: CommunityMetadata,
  deletedAwards: Set<string>
): { award: BadgeAward; reason: string }[] {
  const invalid: { award: BadgeAward; reason: string }[] = [];
  
  for (const award of awards) {
    const chain = validateAuthorityChain(award, community, awards, deletedAwards);
    if (chain && !chain.isValid) {
      invalid.push({ award, reason: chain.invalidReason || 'Unknown reason' });
    }
  }
  
  return invalid;
}

// Get the full authority path for display
export function getAuthorityPath(chain: AuthorityChain): string[] {
  const path: string[] = [];
  
  let current: AuthorityChain | undefined = chain;
  while (current) {
    path.unshift(`${current.granter.slice(0, 8)}... → ${current.badge.identifier}`);
    current = current.parentChain;
  }
  
  return path;
}

// Get the depth of an authority chain
export function getChainDepth(chain: AuthorityChain): number {
  let depth = 1;
  let current = chain.parentChain;
  while (current) {
    depth++;
    current = current.parentChain;
  }
  return depth;
}
